/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../theme/theme';
import {
  Meridiem,
  TimeModificationAction
} from '../daterangepicker/types';
import ClockDial from './ClockDial';
import {ClockDialView} from './ClockDialUtils';

const getDisplayHour = (hour: number) => (
  (hour % 12 || 12).toString().padStart(2, '0')
);

const getDisplayMinute = (minute: number) => (
  minute.toString().padStart(2, '0')
);

// defines the styles used by the component
const useStyles = makeStyles(() => ({
  timePickerWrapper : {
    gap            : '10px',
    width          : '95%',
    display        : 'flex',
    padding        : '16px 0',
    flexWrap       : 'wrap',
    alignItems     : 'center',
    flexDirection  : 'row',
    justifyContent : 'center'
  },
  digitColumn : {
    flex           : '0.5 1 auto',
    display        : 'flex',
    alignItems     : 'center',
    flexDirection  : 'row',
    justifyContent : 'center'
  },
  hourBox : {
    flex           : '0 1 96px',
    width          : '100%',
    height         : '80px',
    color          : dlsColor('darkTeal'),
    border         : `1px solid ${dlsColor('arcticBlue')}`,
    cursor         : 'pointer',
    display        : 'flex',
    fontSize       : '48px',
    fontFamily     : 'Inter',
    maxWidth       : '96px',
    background     : dlsColor('aquamarine'),
    alignItems     : 'center',
    borderRadius   : '8px',
    justifyContent : 'center'
  },
  minuteBox : {
    flex           : '0 1 96px',
    width          : '100%',
    height         : '80px',
    color          : dlsColor('grey2'),
    cursor         : 'pointer',
    display        : 'flex',
    fontSize       : '48px',
    fontFamily     : 'Inter',
    maxWidth       : '96px',
    background     : dlsColor('grey6'),
    alignItems     : 'center',
    borderRadius   : '8px',
    justifyContent : 'center'
  },
  colon : {
    color          : dlsColor('grey1'),
    display        : 'flex',
    padding        : '0 8px',
    fontSize       : '57px',
    fontFamily     : 'Inter',
    alignSelf      : 'flex-start',
    fontWeight     : 400,
    lineHeight     : '64px',
    paddingTop     : '8px',
    alignItems     : 'center',
    letterSpacing  : '0px',
    verticalAlign  : 'middle',
    justifyContent : 'center'
  },
  meridiemToggleBox : {
    width         : '52px',
    height        : '80px',
    border        : `1px solid ${dlsColor('grey1')}`,
    display       : 'flex',
    overflow      : 'hidden',
    boxShadow     : '0px 1px 4px 0px #00000040',
    background    : dlsColor('grey8'),
    flexShrink    : 0,
    borderRadius  : '8px',
    flexDirection : 'column'
  },
  amBox : {
    height         : '40px',
    display        : 'flex',
    cursor         : 'pointer',
    fontSize       : '16px',
    fontFamily     : 'Inter',
    fontWeight     : 600,
    alignItems     : 'center',
    borderBottom   : `1px solid ${dlsColor('white')}`,
    justifyContent : 'center'
  },
  pmBox : {
    height         : '40px',
    display        : 'flex',
    cursor         : 'pointer',
    fontSize       : '16px',
    fontFamily     : 'Inter',
    fontWeight     : 600,
    alignItems     : 'center',
    justifyContent : 'center'
  },
  activeMeridiem : {
    color      : dlsColor('darkTeal'),
    background : dlsColor('paleBlue')
  },
  inactiveMeridiem : {
    color : dlsColor('grey2')
  },
  activeDigitBox : {
    backgroundColor : dlsColor('paleBlue')
  }
}));

// defines the prop definitions for the component
export interface DigitalTimePickerProps {
  dateValue?: Date;
  onChangeTime?: (action: TimeModificationAction) => void;
  onSelectTime?: (hour: number, minute: number) => void;
}

/**
 * DigitalTimePicker that utilizes material ui's v4 date time picker input
 * with a popper that displays the clock for time selection .
 *
 * ```typescript
 *
 * import {DateTimePicker} from '@ion/dls/src/components/date';
 *
 * <DateTimePicker value={momentValue}
 *   onChange={onChange}
 *   locale='fr'
 *   fieldLabel='Choose date & time' />
 *
 * ```
 */
export const DigitalTimePicker = ({
  dateValue,
  onChangeTime,
  onSelectTime
}: DigitalTimePickerProps) => {
  const classes         = useStyles();
  const rootRef         = useRef<HTMLDivElement | null>(null);
  const selectedDate    = dateValue ?? new Date();
  const currentMeridiem = selectedDate.getHours() >= 12 ? Meridiem.PM : Meridiem.AM;
  const [activeTimeView, setActiveTimeView] = useState<ClockDialView | null>(null);
  const selectedHour = selectedDate.getHours();
  const selectedHour12 = selectedHour % 12 || 12;
  const selectedMinute = selectedDate.getMinutes();

  const openClock = useCallback((view: ClockDialView) => (event: MouseEvent<HTMLElement>) => {
    if (onSelectTime) {
      setActiveTimeView(previousView => previousView === view ? null : view);
    } else {
      onChangeTime?.(view === ClockDialView.Hours
        ? TimeModificationAction.HourUp
        : TimeModificationAction.MinuteUp);
    }
  }, [onChangeTime, onSelectTime]);

  const onCloseClock = useCallback(() => {
    setActiveTimeView(null);
  }, []);

  useEffect(() => {
    if (activeTimeView) {
      const onDocumentMouseDown = (event: globalThis.MouseEvent) => {
        if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
          onCloseClock();
        }
      };

      document.addEventListener('mousedown', onDocumentMouseDown);
      return () => {
        document.removeEventListener('mousedown', onDocumentMouseDown);
      };
    }

    return undefined;
  }, [
    activeTimeView,
    onCloseClock
  ]);

  const onSelectClockHour = useCallback((value: number) => {
    const isPM = currentMeridiem === Meridiem.PM;
    const hour = isPM
      ? (value % 12) + 12
      : value % 12;
    onSelectTime?.(hour, selectedMinute);
  }, [
    currentMeridiem,
    onSelectTime,
    selectedMinute
  ]);

  const onSelectClockMinute = useCallback((value: number) => {
    onSelectTime?.(selectedHour, value);
  }, [onSelectTime, selectedHour]);

  const onHourChange = useCallback((event: MouseEvent<HTMLElement>) => {
    openClock(ClockDialView.Hours)(event);
  }, [openClock]);

  const onMinuteChange = useCallback((event: MouseEvent<HTMLElement>) => {
    openClock(ClockDialView.Minutes)(event);
  }, [openClock]);

  const onMeridiemChange = useCallback((target: Meridiem) => {
    if (currentMeridiem !== target) {
      onChangeTime?.(TimeModificationAction.ToggleMeridiem);
    }
  }, [currentMeridiem, onChangeTime]);

  const onAMChange = useCallback(() => onMeridiemChange(Meridiem.AM), [onMeridiemChange]);

  const onPMChange = useCallback(() => onMeridiemChange(Meridiem.PM), [onMeridiemChange]);

  return (
    <div className={classes.timePickerWrapper}
      ref={rootRef}>
      <div className={classes.digitColumn}>
        <div className={classnames(classes.hourBox, {
          [classes.activeDigitBox] : activeTimeView === ClockDialView.Hours
        })}
        aria-label={translate('common:editors.dateTime.hours')}
        onClick={onHourChange}>
          {getDisplayHour(selectedHour)}
        </div>
        <div className={classes.colon}>{translate('common:punctuation.colon')}</div>
        <div className={classnames(classes.minuteBox, {
          [classes.activeDigitBox] : activeTimeView === ClockDialView.Minutes
        })}
        aria-label={translate('common:editors.dateTime.minutes')}
        onClick={onMinuteChange}>
          {getDisplayMinute(selectedMinute)}
        </div>
      </div>
      <div className={classes.meridiemToggleBox}>
        <div className={classnames(classes.amBox, {
          [classes.activeMeridiem]   : currentMeridiem === Meridiem.AM,
          [classes.inactiveMeridiem] : currentMeridiem !== Meridiem.AM
        })}
        onClick={onAMChange}>
          {translate('common:editors.dateTime.am')}
        </div>
        <div className={classnames(classes.pmBox, {
          [classes.activeMeridiem]   : currentMeridiem === Meridiem.PM,
          [classes.inactiveMeridiem] : currentMeridiem !== Meridiem.PM
        })}
        onClick={onPMChange}>
          {translate('common:editors.dateTime.pm')}
        </div>
      </div>
      <ClockDial view={activeTimeView}
        selectedHour={selectedHour12}
        selectedMinute={selectedMinute}
        onSelectHour={onSelectClockHour}
        onSelectMinute={onSelectClockMinute}
        onClose={onCloseClock} />
    </div>
  );
};

export default DigitalTimePicker;
